import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Select,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";

import { AppRoutes } from "src/common/constants/constantEnums";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { HindiLabels } from "src/common/constants/hindiLabels";
import type { RootState } from "src/store/store";
import matrimony_hero from "src/resources/images/matrimony_hero.png";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export const Matrimony: React.FC = () => {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();

  const [lookingFor, setLookingFor] = useState<"Bride" | "Groom">("Bride");
  const [maritalStatus, setMaritalStatus] = useState<string>("Never Married");
  const [location, setLocation] = useState<string>("Thane, Maharashtra");

  const initialSearchParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("gender", lookingFor === "Bride" ? "FEMALE" : "MALE");
    params.set("maritalStatus", maritalStatus);
    params.set("nativePlace", location);
    params.set("page", "1");
    params.set("size", "10");
    return params;
  }, [lookingFor, maritalStatus, location]);

  const onSearch = () => {
    navigate(`${AppRoutes.MATRIMONY_SEARCH_RESULTS}?${initialSearchParams.toString()}`);
  };

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const isHindiLabels = globalInfo.globalLanguage === "HI";

  // Helper to pick label from enums
  const t = <K extends keyof typeof EnglishLabels & keyof typeof HindiLabels>(key: K) =>
    (isHindiLabels ? (HindiLabels as any)[key] : (EnglishLabels as any)[key]) as string;


  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      minHeight="100vh"
      bg="#FFF"
      height="100%"
    >
      {/* <Box position="sticky" top={0} zIndex={1000} width="100%">
        <MatrimonyNavbar
          isScrolled={isScrolled}
          activeSection={t("Matrimony")}
        />
      </Box> */}
      {/* Hero Section */}
      <Box position="relative" width="100%" bg="#000">
        <Image
          src={matrimony_hero}
          alt="Matrimony Hero"
          width="100%"
          height={isMobile ? "420px" : "520px"}
          objectFit="cover"
          opacity={0.9}
        />

        {/* Hero overlay content */}
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          px={{ base: 4, md: 8 }}
          pt={{ base: "92px", md: "104px" }}
        >
        </Flex>
      </Box>

      {/* Search Card (starts after Hero section) */}
      <Flex
        width="100%"
        justify="center"
        bg="transparent"
        px={{ base: 4, md: 8 }}
        mt={{ base: "-72px", md: "-84px" }}
        pb={{ base: 8, md: 10 }}
        position="relative"
        zIndex={1}
      >
        <Box
          width={{ base: "100%", sm: "380px", md: "460px" }}
          bg="#FFFFFF"
          borderRadius="24px"
          boxShadow="0px 24px 40px rgba(0, 0, 0, 0.15)"
          p={{ base: 4, md: 6 }}
        >
          <Stack spacing={4}>
            <Box>
              <Text fontSize="xs" fontWeight="600" color="#6C757D" mb={1}>
                {isHindiLabels ? "मैं ढूँढ रहा/रही हूँ" : "I'm looking for"}
              </Text>
              <Select
                value={lookingFor}
                onChange={(e) => setLookingFor(e.target.value as "Bride" | "Groom")}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              >
                <option>{isHindiLabels ? "दुल्हन" : "Bride"}</option>
                <option>{isHindiLabels ? "दूल्हा" : "Groom"}</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight="600" color="#6C757D" mb={1}>
                {isHindiLabels ? "वैवाहिक स्थिति" : "Marital Status"}
              </Text>
              <Select
                value={maritalStatus}
                onChange={(e) => setMaritalStatus(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              >
                <option>{isHindiLabels ? "अविवाहित" : "Bachelor"}</option>
                <option>{isHindiLabels ? "तलाकशुदा" : "Divorced"}</option>
                <option>{isHindiLabels ? "विधवा/विधुर" : "Widowed"}</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight="600" color="#6C757D" mb={1}>
                {isHindiLabels ? "स्थान" : "Location"}
              </Text>
              <Select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              >
                <option>Thane, Maharashtra</option>
                <option>Mumbai, Maharashtra</option>
                <option>Pune, Maharashtra</option>
              </Select>
            </Box>

            <Button
              mt={2}
              width="100%"
              height="48px"
              borderRadius="12px"
              bg="#4A2C2A"
              color="#FFFFFF"
              _hover={{ bg: "#3A211F" }}
              onClick={onSearch}
            >
              {isHindiLabels ? "प्रोफ़ाइल खोजें" : "Search Profile"}
            </Button>
          </Stack>
        </Box>
      </Flex>

      {/* Shloka + Divider section */}
      <Flex
        direction="column"
        alignItems="center"
        py={{ base: 8, md: 10 }}
        px={{ base: 4, md: 0 }}
        bgGradient="linear(to-b, #FFF, #FDF3E7)"
        width="100%"
      >
        <Text
          fontSize={{ base: "sm", md: "md" }}
          textAlign="center"
          color="#6A1B1A"
          maxW="640px"
          mb={2}
        >
          यस्त्वात्मरतिरेव स्यादात्मतृप्तश्च मानवः |
          आत्मन्येव च सन्तुष्टस्तस्य कार्यं न विद्यते ||
        </Text>
        <Text fontSize="xs" color="#6C757D" textAlign="center">
          Bhagavad Gita: Chapter 3, Verse 17
        </Text>
      </Flex>

      {/* Happy Couples */}
      <Box
        width="100%"
        bg="#FDF3E7"
        py={{ base: 8, md: 12 }}
        px={{ base: 4, md: 12 }}
      >
        <Heading
          textAlign="center"
          mb={{ base: 6, md: 8 }}
          fontSize={{ base: "24px", md: "32px" }}
          color="#4A2C2A"
        >
          {isHindiLabels ? "सुखी जोड़े" : "Happy Couples"}
        </Heading>

        <Stack
          spacing={4}
          maxW="720px"
          mx="auto"
        >
          {["Vikram & Anjali", "Rohit & Neha", "Amit & Pooja"].map(
            (name, index) => (
              <Flex
                key={name}
                bg="#FFFFFF"
                borderRadius="16px"
                boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)"
                border="1px solid rgba(108, 117, 125, 0.18)"
                p={4}
                align="center"
              >
                <Image
                  src={`/happy-couple-${index + 1}.jpg`}
                  alt={name}
                  boxSize="56px"
                  borderRadius="full"
                  objectFit="cover"
                  mr={4}
                />
                <Box>
                  <Text fontWeight="600" color="#4A2C2A" mb={1}>
                    {name}
                  </Text>
                  <Text fontSize="sm" color="#6C757D">
                    {index === 0 &&
                      "Found my soulmate within 2 weeks of joining. Thank you for this amazing platform."}
                    {index === 1 &&
                      "The verification process gave us trust. Families connected instantly."}
                    {index === 2 &&
                      "Simple interface, genuine profiles. Highly recommend for our community."}
                  </Text>
                </Box>
              </Flex>
            )
          )}
        </Stack>
      </Box>

      {/* Get in Touch Section */}
      <Box width="100%" bg="#4A2C2A" color="#FFFFFF" py={{ base: 8, md: 10 }}>
        <Flex
          direction="column"
          alignItems="center"
          textAlign="center"
          px={{ base: 4, md: 12 }}
        >
          <Heading
            fontSize={{ base: "22px", md: "28px" }}
            mb={2}
          >
            {t("Get_In_Touch_With_Us")}
          </Heading>
          <Text
            maxW="560px"
            fontSize={{ base: "sm", md: "md" }}
            mb={6}
          >
            {t("Get_In_Touch_With_Us_Des")}
          </Text>

          <Flex gap={3} wrap="wrap" justify="center" mb={6}>
            {[
              "email",
              "whatsapp",
              "facebook",
              "instagram",
            ].map((icon) => (
              <Box
                key={icon}
                width="44px"
                height="44px"
                borderRadius="12px"
                bg="#FFFFFF"
                opacity={0.95}
              />
            ))}
          </Flex>

          <Box textAlign="center" maxW="720px">
            <Heading
              fontSize={{ base: "18px", md: "20px" }}
              mb={2}
            >
              Maurya Kushwaha Samaj, Thane
            </Heading>
            <Text fontSize="sm" mb={6}>
              Bringing our community together, one match at a time. Trust, Tradition,
              and Technology combined.
            </Text>

            <Flex
              direction={{ base: "column", md: "row" }}
              justify="center"
              gap={{ base: 2, md: 6 }}
              fontSize="xs"
            >
              <Text>{t("Privacy_Policy")}</Text>
              <Text>{isHindiLabels ? "हमारे बारे में" : "About Us"}</Text>
              <Text>{t("Contact_Support")}</Text>
            </Flex>

            <Text mt={4} fontSize="xs" opacity={0.8}>
              © 2024, All rights reserved. Powered by VESIS
            </Text>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};
